/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  CronDescriptor,
  CronDescriptorOccurrence
} from '~backoffice/app/~shared/models/shared.models';
import { delay, tap } from 'rxjs';
import dayjs from '~backoffice/app/~shared/libs/dayjs.lib';
import {
  fillMissingInteger,
  isContinuousSequenceOfNumber,
  pickFirstAndLast
} from '~backoffice/app/~shared/utils/utils';

@Component({
  selector: 'cron-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatNativeDateModule,
    MatDatepickerModule
  ],
  templateUrl: './cron-editor.component.html',
  styleUrls: ['./cron-editor.component.scss']
})
export class CronEditorComponent implements OnInit, OnChanges {
  @Input() cron: string | null | undefined;
  currentCron: string | null | undefined;
  @Output() cronChange = new EventEmitter<string>();
  @Output() dateChange = new EventEmitter<string>();

  cronDescriptor: CronDescriptor | undefined;
  cronDescriptorOccurrence = CronDescriptorOccurrence;

  form = new FormGroup({
    occurrence: new FormControl('', { validators: Validators.required }),
    date: new FormControl(''),
    hour: new FormControl('00:00'),
    month: new FormControl<number[]>([1]),
    dayOfMonth: new FormControl(1),
    daysOfWeek: new FormControl<number[]>([1]),
    minute: new FormControl(1, { validators: Validators.required })
  });

  formChanges$ = this.form.valueChanges.pipe(
    delay(500),
    tap({
      next: values => {
        if (!this.isFormValid) {
          this.cronChange.emit('');
          return;
        }
        this.cronDescriptor = {
          ...this.cronDescriptor,
          ...values
        } as CronDescriptor;
        this.generateCronExpression();
      }
    })
  );

  ngOnInit(): void {
    this.fillValuesFromCronExpression(this.cron);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('cron' in changes && this.cron !== this.currentCron) {
      this.fillValuesFromCronExpression(this.cron);
    }
  }

  generateCronExpression(): void {
    let cronExpression = '';
    this.currentCron = cronExpression;
    const cronDescriptor = this.cronDescriptor;

    if (!cronDescriptor || (!cronDescriptor.hour && !cronDescriptor.minute))
      return;

    if (
      cronDescriptor.occurrence === CronDescriptorOccurrence.date &&
      cronDescriptor.hour &&
      cronDescriptor.date
    ) {
      const [hours, minutes] = cronDescriptor.hour.split(':');
      cronExpression = dayjs(cronDescriptor.date)
        .set('hours', parseInt(hours) || 0)
        .set('minutes', parseInt(minutes) || 0)
        .set('seconds', 0)
        .format();
      this.currentCron = cronExpression;
      this.dateChange.emit(cronExpression);
      return;
    }

    const [hours, minutes] = (cronDescriptor.hour || '00:00').split(':');

    const cronParts = [minutes, hours, '*', '*', '*'];

    if (cronDescriptor.occurrence === CronDescriptorOccurrence.everyHour) {
      cronParts[1] = '*';
      cronParts[0] = cronDescriptor.minute.toString();
    } else if (
      cronDescriptor.occurrence === CronDescriptorOccurrence.everyWeek
    ) {
      const areDaysContinuous = isContinuousSequenceOfNumber(
        cronDescriptor.daysOfWeek
      );
      cronParts[4] = areDaysContinuous
        ? pickFirstAndLast(cronDescriptor.daysOfWeek).join('-')
        : cronDescriptor.daysOfWeek.join(',');
    } else if (
      cronDescriptor.occurrence === CronDescriptorOccurrence.everyMonth
    ) {
      cronParts[2] = cronDescriptor.dayOfMonth.toString();
    } else if (
      cronDescriptor.occurrence === CronDescriptorOccurrence.everyYear
    ) {
      cronParts[2] = cronDescriptor.dayOfMonth.toString();
      const areMonthsContinuous = isContinuousSequenceOfNumber(
        cronDescriptor.month
      );
      cronParts[3] = areMonthsContinuous
        ? pickFirstAndLast(cronDescriptor.month).join('-')
        : cronDescriptor.month.join(',');
    }

    cronExpression = cronParts.join(' ');
    this.currentCron = cronExpression;
    this.cronChange.emit(cronExpression);
  }

  fillValuesFromCronExpression(
    cronExpression: string | null | undefined
  ): void {
    const cronDescriptor = {
      hour: null,
      month: null,
      dayOfMonth: null,
      daysOfWeek: [],
      minute: null,
      date: null
    } as unknown as CronDescriptor;

    if (!cronExpression) {
      if (!this.cronDescriptor) {
        cronDescriptor.occurrence = null;
      }

      this.cronDescriptor = { ...this.form.value, ...cronDescriptor };
      this.form.patchValue(this.cronDescriptor);
      return;
    }

    if (!cronExpression.includes(' ')) {
      cronDescriptor.occurrence = CronDescriptorOccurrence.date;
      cronDescriptor.date = cronExpression;
      cronDescriptor.hour = dayjs(cronExpression).format('HH:mm');
      this.cronDescriptor = cronDescriptor;
      this.form.patchValue(this.cronDescriptor);
      return;
    }

    const expressionParts = cronExpression.split(' ');
    if (expressionParts.length < 5) return;
    const dayOfMonth = parseInt(expressionParts[2]) || 0;
    let month: number[] = [];
    if (expressionParts[3].includes(',')) {
      month = expressionParts[3]
        .split(',')
        .map(el => parseInt(el))
        .filter(Boolean)
        .sort((a, b) => a - b);
    } else if (expressionParts[3].includes('-')) {
      month = expressionParts[3]
        .split('-')
        .map(el => parseInt(el))
        .filter(Boolean)
        .sort((a, b) => a - b);
      month = fillMissingInteger(month);
    } else {
      month = [parseInt(expressionParts[3])].filter(Boolean);
    }

    let daysOfWeek: number[] = [];
    if (expressionParts[4].includes(',')) {
      daysOfWeek = expressionParts[4]
        .split(',')
        .map(el => parseInt(el))
        .filter(val => !isNaN(val))
        .sort((a, b) => a - b);
    } else if (expressionParts[4].includes('-')) {
      daysOfWeek = expressionParts[4]
        .split('-')
        .map(el => parseInt(el))
        .filter(val => !isNaN(val))
        .sort((a, b) => a - b);
      daysOfWeek = fillMissingInteger(daysOfWeek);
    } else {
      daysOfWeek = [parseInt(expressionParts[4])].filter(val => !isNaN(val));
    }

    const minutes = parseInt(expressionParts[0]);
    const hours = parseInt(expressionParts[1]);
    cronDescriptor.minute = minutes;
    cronDescriptor.hour = dayjs()
      .set('hours', hours || 0)
      .set('minutes', minutes || 0)
      .set('seconds', 0)
      .format('HH:mm');
    cronDescriptor.month = month;
    cronDescriptor.dayOfMonth = dayOfMonth;
    cronDescriptor.daysOfWeek = daysOfWeek;

    if (month.length) {
      cronDescriptor.occurrence = CronDescriptorOccurrence.everyYear;
    } else if (!month.length && !!dayOfMonth) {
      cronDescriptor.occurrence = CronDescriptorOccurrence.everyMonth;
    } else if (daysOfWeek.length && !isNaN(hours)) {
      cronDescriptor.occurrence = CronDescriptorOccurrence.everyWeek;
    } else if (!hours) {
      cronDescriptor.occurrence = CronDescriptorOccurrence.everyHour;
    } else {
      cronDescriptor.occurrence = CronDescriptorOccurrence.everyDay;
    }

    this.cronDescriptor = cronDescriptor;
    this.form.patchValue(this.cronDescriptor);
  }

  get selectedOccurrence(): CronDescriptorOccurrence | null | undefined {
    return this.form.get('occurrence')?.value as
      | CronDescriptorOccurrence
      | null
      | undefined;
  }

  get currentDate(): string | null | undefined {
    return this.form.get('date')?.value as
      | CronDescriptorOccurrence
      | null
      | undefined;
  }

  get isFormValid(): boolean {
    const { occurrence, date, daysOfWeek, dayOfMonth, hour, month } =
      this.form.value;
    if (
      !occurrence ||
      (occurrence !== CronDescriptorOccurrence.everyHour && !hour)
    )
      return false;

    if (occurrence === CronDescriptorOccurrence.date && !!date) {
      return true;
    }

    if (
      occurrence === CronDescriptorOccurrence.everyHour &&
      this.form.get('minute')?.valid
    ) {
      return true;
    }
    if (occurrence === CronDescriptorOccurrence.everyDay && hour) {
      return true;
    }
    if (
      occurrence === CronDescriptorOccurrence.everyWeek &&
      daysOfWeek?.length
    ) {
      return true;
    }

    if (occurrence === CronDescriptorOccurrence.everyMonth && dayOfMonth) {
      return true;
    }

    if (
      occurrence === CronDescriptorOccurrence.everyYear &&
      dayOfMonth &&
      month
    ) {
      return true;
    }

    return false;
  }
}
